import { useEffect, useMemo } from 'react';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useTenantLocale } from '@/hooks/useTenantLocale';
import { citiesFor, COUNTRIES, postalCodeSpecFor, statesFor } from '@/lib/geography';
import type { Address } from '@/types/common';

interface Props {
  value: Address | undefined;
  onChange: (next: Address) => void;
  /** Field-level required marker propagated to every leaf. */
  required?: boolean;
  /** Slug prefix for input ids so multiple AddressFields can coexist on a page. */
  idPrefix?: string;
  /**
   * Hides the line1 / line2 inputs — useful when the parent renders
   * its own street fields (e.g. the customer-address sheet that
   * already had its own copy) and only wants the canonical pickers
   * for city / state / postal / country.
   */
  hideLines?: boolean;
}

/**
 * Canonical address input block — country picker drives the state +
 * postal-code shape. India tenants get a real state dropdown
 * (28 states + 8 UTs with GSTIN-compatible codes); other countries
 * fall back to free text. Postal code label + format hint flips per
 * country.
 *
 * Designed to be a drop-in for the inline "Address" blocks scattered
 * across admin forms — call sites pass the current Address +
 * `onChange` and the component handles every field. Country defaults
 * to the tenant locale when unset so the operator sees the right
 * state list immediately on a fresh form.
 */
export function AddressFields({
  value,
  onChange,
  required,
  idPrefix = 'addr',
  hideLines,
}: Props) {
  const locale = useTenantLocale();
  const country = (value?.country || locale.country).toUpperCase();
  const stateList = useMemo(() => statesFor(country), [country]);
  const postalSpec = useMemo(() => postalCodeSpecFor(country), [country]);
  // Curated typeahead list — empty (or null) means no suggestions.
  // Browser <datalist> handles the suggestion UI natively; the input
  // stays free-text so any unlisted city still works.
  const citySuggestions = useMemo(
    () => citiesFor(country, value?.state) ?? [],
    [country, value?.state],
  );
  const cityListId = citySuggestions.length > 0 ? `${idPrefix}-cities` : undefined;

  // Seed the country on first paint so downstream pickers know which
  // state list to show. Without this, the operator sees a generic
  // free-text state input until they touch the country dropdown.
  useEffect(() => {
    if (!value?.country) {
      onChange({ ...value, country: locale.country });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof Address>(k: K, v: Address[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <div className="space-y-3">
      {!hideLines && (
        <>
          <FormField label="Line 1" htmlFor={`${idPrefix}Line1`} required={required}>
            <Input
              id={`${idPrefix}Line1`}
              value={value?.line1 ?? ''}
              onChange={(e) => set('line1', e.target.value)}
              placeholder="Building / street / locality"
              required={required}
            />
          </FormField>
          <FormField label="Line 2" htmlFor={`${idPrefix}Line2`}>
            <Input
              id={`${idPrefix}Line2`}
              value={value?.line2 ?? ''}
              onChange={(e) => set('line2', e.target.value)}
              placeholder="Optional — landmark, area, unit"
            />
          </FormField>
        </>
      )}

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Country" htmlFor={`${idPrefix}Country`}>
          <Select
            value={country}
            onValueChange={(v) => {
              // Clear state when country changes — the existing state
              // value is meaningless under the new country's list.
              onChange({ ...value, country: v, state: undefined });
            }}
          >
            <SelectTrigger id={`${idPrefix}Country`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  <span className="inline-flex items-center gap-2">
                    <span className="font-mono text-[10.5px] text-muted-foreground">
                      {c.code}
                    </span>
                    <span>{c.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField label="State / region" htmlFor={`${idPrefix}State`} required={required}>
          {stateList ? (
            <Select value={value?.state ?? ''} onValueChange={(v) => set('state', v)}>
              <SelectTrigger id={`${idPrefix}State`}>
                <SelectValue placeholder="Pick a state…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {stateList.map((s) => (
                  <SelectItem key={s.code} value={s.code}>
                    <span className="inline-flex items-center gap-2">
                      <span className="font-mono text-[10.5px] text-muted-foreground">
                        {s.code}
                      </span>
                      <span>{s.name}</span>
                      {s.kind === 'ut' && (
                        <Badge variant="muted" className="text-[9px]">
                          UT
                        </Badge>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id={`${idPrefix}State`}
              value={value?.state ?? ''}
              onChange={(e) => set('state', e.target.value)}
              placeholder="State / region / province"
              required={required}
            />
          )}
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FormField
          label="City"
          htmlFor={`${idPrefix}City`}
          required={required}
          hint={
            citySuggestions.length > 0
              ? 'Start typing — pick from the suggestions or enter your own.'
              : undefined
          }
        >
          <Input
            id={`${idPrefix}City`}
            value={value?.city ?? ''}
            onChange={(e) => set('city', e.target.value)}
            placeholder="City / town"
            required={required}
            list={cityListId}
            autoComplete="address-level2"
          />
          {cityListId && (
            <datalist id={cityListId}>
              {citySuggestions.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          )}
        </FormField>
        <FormField
          label={postalSpec.label}
          htmlFor={`${idPrefix}Postal`}
          hint={postalSpec.hint}
        >
          <Input
            id={`${idPrefix}Postal`}
            value={value?.pincode ?? ''}
            onChange={(e) => set('pincode', e.target.value)}
            placeholder={postalSpec.placeholder}
            pattern={postalSpec.pattern}
            maxLength={postalSpec.maxLength}
            inputMode="numeric"
            className="font-mono"
          />
        </FormField>
      </div>
    </div>
  );
}
